import "./OverviewSection.css";

import { LANDHOLDING_REFERENCE_TYPE_OPTIONS } from "../../../../../../../../constants";
import { ServiceError } from "../../../../../../../../service/Shared";
import { Button, Column, ErrorBox, Modal, Row, Select, TextArea, TextInput } from "../../../../../../../../widget";
import { useOverviewSection } from "./useOverviewSection";

export const OverviewSection = (): JSX.Element => {
  const {
    projectGroupData,
    formData,
    countryOptions,
    errors,
    deleteErrors,
    setField,
    onSubmit,
    isOnSubmitLoading,
    isLandholdingManagement,
    showDeleteModal,
    setShowDeleteModal,
    onDeleteConfirm,
    isDeleteLoading,
  } = useOverviewSection();

  return (
    <div className="OverviewSection">
      <div className="OverviewSectionHeader">
        <h2>Overview</h2>
        <div className="OverviewSectionButtons">
          {isLandholdingManagement && (
            <Button text="Delete landholding" variant="tertiary" onClick={() => setShowDeleteModal(true)} />
          )}
          <Button text="Save" onClick={onSubmit} isLoading={isOnSubmitLoading} />
        </div>
      </div>
      {errors && errors.length > 0 && (
        <Row spacingV="ml" justify="start">
          <Column span={10}>
            <ErrorBox
              title="Please solve the below issues before saving"
              text={errors.map((error: ServiceError) => error.message)}
            />
          </Column>
        </Row>
      )}
      <Row spacingV="xl">
        <Column span={4}>
          <Row spacingV="xl">
            <Column span={12}>
              <TextInput
                label={isLandholdingManagement ? "Display name" : "Registry group name"}
                name="groupName"
                value={formData.groupName}
                onChange={setField("groupName")}
              />
            </Column>
          </Row>
          {isLandholdingManagement && (
            <>
              <Row spacingV="xl">
                <Column span={12}>
                  <TextInput
                    label="Gross area (ha)"
                    name="areaGrossHa"
                    type="number"
                    value={formData.areaGrossHa}
                    onChange={setField("areaGrossHa")}
                  />
                </Column>
              </Row>
              <Row>
                <Column span={12}>
                  <TextInput label="Region" name="region" value={formData.region} onChange={setField("region")} />
                </Column>
              </Row>
            </>
          )}
          {!isLandholdingManagement && (
            <Row>
              <Column span={12}>
                <TextInput label="Registry group ID" name="groupId" value={projectGroupData?.groupId || ""} disabled />
              </Column>
            </Row>
          )}
        </Column>
        {isLandholdingManagement && (
          <Column span={4}>
            <Row spacingV="xl">
              <Column span={12}>
                <Select
                  label="Landholding reference"
                  value={formData.externalParty ?? ""}
                  onChange={setField("externalParty")}
                  data={LANDHOLDING_REFERENCE_TYPE_OPTIONS}
                  placeholder="Select"
                />
              </Column>
            </Row>
            <Row spacingV="xl">
              <Column span={12}>
                <TextInput
                  label="Landholding reference ID"
                  name="landholdingReferenceId"
                  value={formData.externalReference}
                  onChange={setField("externalReference")}
                />
              </Column>
            </Row>
            <Row>
              <Column span={12}>
                <Select
                  label="Country"
                  value={formData.country}
                  onChange={setField("country")}
                  data={countryOptions}
                  placeholder="Select"
                />
              </Column>
            </Row>
          </Column>
        )}
        <Column span={isLandholdingManagement ? 4 : 5} offset={isLandholdingManagement ? 0 : 1}>
          <TextArea
            name="Description"
            label={isLandholdingManagement ? "Description" : "Registry group description"}
            value={formData.projectGroupDescription || ""}
            onChange={setField("projectGroupDescription")}
            displayCharacterCount
            maxLength={4000}
          />
        </Column>
      </Row>
      {isLandholdingManagement && (
        <Row>
          <Column span={4}>
            <TextInput label="Latitude" name="latitude" value={formData.latitude} onChange={setField("latitude")} />
          </Column>
          <Column span={4}>
            <TextInput label="Longitude" name="longitude" value={formData.longitude} onChange={setField("longitude")} />
          </Column>
        </Row>
      )}
      {isLandholdingManagement && (
        <Modal show={showDeleteModal} title="Delete landholding" onClose={() => setShowDeleteModal(false)}>
          {deleteErrors && deleteErrors.length > 0 && (
            <Row spacingV="ml" justify="center">
              <Column span={8}>
                <ErrorBox
                  title="Unable to delete landholding"
                  text={deleteErrors.map((error: ServiceError) => error.message)}
                />
              </Column>
            </Row>
          )}
          <Row spacingV="ll" justify="center">
            <Column span={10}>
              <p className="body1">Are you sure you want to delete this landholding? This action cannot be undone.</p>
            </Column>
          </Row>
          <Row justify="center">
            <div className="OverviewSectionDeleteButtons">
              <Button onClick={onDeleteConfirm} text="Delete landholding" isLoading={isDeleteLoading} />
              <Button onClick={() => setShowDeleteModal(false)} text="Cancel" variant="secondary" />
            </div>
          </Row>
        </Modal>
      )}
    </div>
  );
};
